import MapboxDraw from '@mapbox/mapbox-gl-draw';
import doubleClickZoom from './utils/double_click_zoom';
import {
  calculateSnapTolerance,
  clearSnapMarkerOnMap,
  sanitizeSnapOptions,
  setSnapMarkerOnMap,
  type SnapHitKind,
} from './utils/CalculateSnapTolerance';
import { cursors, geojsonTypes, modes, types } from './utils/constants';
import { featureDrawActiveState } from './utils/displayFeatureActiveState';
import { drawModes, drawState, Source } from '../../Types';

type RectangleDrawState = drawState & {
  /** First corner placed; move or second action completes the box. */
  armed: boolean;
  /** Ignore the click that immediately follows mousedown on the same gesture. */
  suppressClickFinish: boolean;
  start: [number, number] | null;
  keepVisibleFeatureIds?: string[];
  _snapLocked?: boolean;
  _snapKind?: SnapHitKind | null;
};

const MIN_RECT_SIDE_DEG = 1e-7;

const DrawAssistedRectangle: drawModes = <drawModes>(<unknown>{
  ...MapboxDraw.modes.draw_polygon,
});

function setAxisAlignedRectangle(
  polygon: drawState['polygon'],
  lng1: number,
  lat1: number,
  lng2: number,
  lat2: number
) {
  const minLng = Math.min(lng1, lng2);
  const maxLng = Math.max(lng1, lng2);
  const minLat = Math.min(lat1, lat2);
  const maxLat = Math.max(lat1, lat2);
  polygon.incomingCoords([
    [
      [minLng, minLat],
      [maxLng, minLat],
      [maxLng, maxLat],
      [minLng, maxLat],
      [minLng, minLat],
    ],
  ]);
}

function resolveLngLat(
  state: RectangleDrawState,
  map: any,
  e: any
): { lng: number; lat: number; locked: boolean; kind?: SnapHitKind } {
  const { lng, lat } = e.lngLat;
  let minDistance = Infinity;
  let desiredCoord: number[] | undefined;
  let snapKind: SnapHitKind | undefined;

  if (state.options.snap) {
    [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(map, e, state);
  }

  const locked = Boolean(
    state.options.snap &&
      desiredCoord &&
      Number.isFinite(minDistance) &&
      minDistance <= (state.options.snapTolerance ?? Infinity)
  );

  if (locked && desiredCoord) {
    state.snappedLng = desiredCoord[0];
    state.snappedLat = desiredCoord[1];
  } else {
    state.snappedLng = lng;
    state.snappedLat = lat;
  }

  state._snapLocked = locked;
  state._snapKind = locked ? snapKind ?? null : null;

  return {
    lng: state.snappedLng,
    lat: state.snappedLat,
    locked,
    kind: state._snapKind ?? undefined,
  };
}

function updateSnapMarker(state: RectangleDrawState, map: any) {
  if (!state.options.snap) {
    clearSnapMarkerOnMap(map);
    return;
  }
  setSnapMarkerOnMap(map, state.snappedLng, state.snappedLat, {
    locked: Boolean(state._snapLocked),
    kind: state._snapKind ?? null,
  });
}

function anchorCorner(ctx: any, state: RectangleDrawState, e: any) {
  const { lng, lat } = resolveLngLat(state, ctx.map, e);
  updateSnapMarker(state, ctx.map);
  state.start = [lng, lat];
  state.armed = true;
  setAxisAlignedRectangle(state.polygon, lng, lat, lng, lat);
}

function finishRectangle(ctx: any, state: drawState, e: any) {
  const rectState = state as RectangleDrawState;
  if (!rectState.armed || !rectState.start) return;

  try {
    ctx.map.dragPan.enable();
  } catch {
    /* ignore */
  }

  const { lng, lat } = resolveLngLat(rectState, ctx.map, e);
  const [lng1, lat1] = rectState.start;

  rectState.start = null;
  rectState.armed = false;
  rectState.suppressClickFinish = false;

  clearSnapMarkerOnMap(ctx.map);

  const tooSmall =
    Math.abs(lng - lng1) < MIN_RECT_SIDE_DEG &&
    Math.abs(lat - lat1) < MIN_RECT_SIDE_DEG;

  if (tooSmall) {
    ctx.deleteFeature([rectState.polygon.id], { silent: true });
    return ctx.changeMode(modes.SIMPLE_SELECT, {}, { silent: true });
  }

  setAxisAlignedRectangle(rectState.polygon, lng1, lat1, lng, lat);
  return ctx.changeMode(modes.SIMPLE_SELECT, {
    featureIds: [rectState.polygon.id],
  });
}

DrawAssistedRectangle.onSetup = function (opts: any) {
  const polygon = this.newFeature({
    type: geojsonTypes.FEATURE,
    properties: {},
    geometry: {
      type: geojsonTypes.POLYGON,
      coordinates: [[]],
    },
  });
  this.addFeature(polygon);

  this.clearSelectedFeatures();
  doubleClickZoom.disable(this);
  this.updateUIClasses({ mouse: cursors.ADD });
  this.activateUIButton(types.POLYGON);
  this.setActionableState({ trash: true });

  const state = {
    polygon,
    start: null,
    armed: false,
    suppressClickFinish: false,
    currentVertexPosition: 0,
    keepVisibleFeatureIds: Array.isArray(opts?.keepVisibleFeatureIds)
      ? opts.keepVisibleFeatureIds.map(String)
      : [],
    options: sanitizeSnapOptions(this._ctx.options),
  } as RectangleDrawState;

  const optionsChangedCallBAck = (options: drawState['options']) => {
    state.options = sanitizeSnapOptions(options);
    if (!state.options.snap) {
      clearSnapMarkerOnMap(this.map);
    }
  };

  state.optionsChangedCallBAck = optionsChangedCallBAck;
  this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

  this.state = state;

  const snapSrc = this.map.getSource('geobox-gl-snap-marker');
  if (snapSrc) {
    this.snapMarkerSource = (snapSrc as Source)._data;
  }

  return state;
};

DrawAssistedRectangle.onMouseDown = DrawAssistedRectangle.onTouchStart = function (
  state: drawState,
  e: any
) {
  const rectState = state as RectangleDrawState;
  if (e.originalEvent?.button != null && e.originalEvent.button !== 0) {
    return;
  }

  try {
    this.map.dragPan.disable();
  } catch {
    /* ignore */
  }

  if (!rectState.armed) {
    anchorCorner(this, rectState, e);
    rectState.suppressClickFinish = true;
  }
};

DrawAssistedRectangle.onDrag = DrawAssistedRectangle.onMouseMove = function (
  state: drawState,
  e: any
) {
  const rectState = state as RectangleDrawState;
  this.map.getCanvasContainer().style.cursor = 'crosshair';

  // Snap feedback must work before the first corner is placed.
  const { lng, lat } = resolveLngLat(rectState, this.map, e);
  updateSnapMarker(rectState, this.map);

  if (!rectState.armed || !rectState.start) {
    this.updateUIClasses({ mouse: cursors.ADD });
    return;
  }

  const [lng1, lat1] = rectState.start;
  setAxisAlignedRectangle(rectState.polygon, lng1, lat1, lng, lat);
  this.updateUIClasses({ mouse: cursors.ADD });
};

DrawAssistedRectangle.onMouseUp = DrawAssistedRectangle.onTouchEnd = function (
  state: drawState,
  e: any
) {
  const rectState = state as RectangleDrawState;
  if (!rectState.armed || !rectState.start) return;
  finishRectangle(this, state, e);
};

DrawAssistedRectangle.onClick = DrawAssistedRectangle.onTap = function (
  state: drawState,
  e: any
) {
  const rectState = state as RectangleDrawState;

  if (!rectState.armed) {
    anchorCorner(this, rectState, e);
    return;
  }

  if (rectState.suppressClickFinish) {
    rectState.suppressClickFinish = false;
    return;
  }

  finishRectangle(this, state, e);
};

DrawAssistedRectangle.onKeyUp = function (state: drawState, e: any) {
  const rectState = state as RectangleDrawState;
  if (e.keyCode === 27) {
    try {
      this.map.dragPan.enable();
    } catch {
      /* ignore */
    }
    clearSnapMarkerOnMap(this.map);
    rectState.start = null;
    rectState.armed = false;
    rectState.suppressClickFinish = false;
    this.deleteFeature([rectState.polygon.id], { silent: true });
    return this.changeMode(modes.SIMPLE_SELECT);
  }

  if (e.keyCode === 13 && rectState.armed && rectState.start) {
    finishRectangle(this, state, {
      lngLat: {
        lng: rectState.snappedLng,
        lat: rectState.snappedLat,
      },
    });
  }
};

DrawAssistedRectangle.onStop = function (state: drawState) {
  const rectState = state as RectangleDrawState;

  try {
    this.map.dragPan.enable();
  } catch {
    /* ignore */
  }

  doubleClickZoom.enable(this);
  this.updateUIClasses({ mouse: cursors.NONE });
  clearSnapMarkerOnMap(this.map);
  this.activateUIButton();

  try {
    (this.map as any).off(
      'draw.snap.options_changed',
      rectState.optionsChangedCallBAck
    );
  } catch {
    /* ignore */
  }

  if (this.getFeature(rectState.polygon.id) === undefined) return;

  if (rectState.polygon.isValid()) {
    this.map.fire('draw.create', {
      features: [rectState.polygon.toGeoJSON()],
    });
  } else {
    this.deleteFeature([rectState.polygon.id], { silent: true });
    this.changeMode(modes.SIMPLE_SELECT, {}, { silent: true });
  }
};

DrawAssistedRectangle.toDisplayFeatures = function (
  state: drawState,
  geojson: any,
  display: any
) {
  const rectState = state as RectangleDrawState;
  geojson.properties.active = featureDrawActiveState(
    geojson.properties.id,
    rectState.polygon.id,
    rectState.keepVisibleFeatureIds
  );
  return display(geojson);
};

DrawAssistedRectangle.onTrash = function (state: drawState) {
  const rectState = state as RectangleDrawState;
  try {
    this.map.dragPan.enable();
  } catch {
    /* ignore */
  }
  clearSnapMarkerOnMap(this.map);
  rectState.start = null;
  rectState.armed = false;
  rectState.suppressClickFinish = false;
  this.deleteFeature([rectState.polygon.id], { silent: true });
  this.changeMode(modes.SIMPLE_SELECT);
};

export default DrawAssistedRectangle;
